import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { MATERIAUX } from 'app/classesTs/Materiax';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import Swal from 'sweetalert2';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-listemateriaux',
  templateUrl: './listemateriaux.component.html',
  styleUrls: ['./listemateriaux.component.scss']
})
export class ListemateriauxComponent implements OnInit, OnDestroy {
  // Material Data (Fournisseurs)
  allMaterials: any[] = [];
  filteredMaterials: any[] = [];
  
  // Filtering & Search
  selectedType: string = 'all';
  searchTerm: string = '';
  materiaux = MATERIAUX;
  
  // Pagination with URL persistence
  currentPage: number = 1;
  itemsPerPage: number = 7;
  totalItems: number = 0;
  
  // Loading State
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // Router subscription
  private routerSubscription: Subscription;
  
  // Data loaded flag
  dataLoaded: boolean = false;
  
  // User info for rights checking
  currentUserId: number | null = null;
  isAdmin: boolean = false;
  userRoles: string[] = [];
  userEmail: string = '';

  constructor(
    private agenciesService: AgenciesService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.restoreStateFromUrl();
      });
  }

  ngOnInit(): void {
    this.getCurrentUserInfo();
    
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      this.restoreStateFromUrl();
      this.loadMaterials();
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Récupère les infos utilisateur depuis sessionStorage et les affiche dans la console
   */
  private getCurrentUserInfo(): void {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const user = JSON.parse(authUser);
        this.currentUserId = user.id || null;
        this.userRoles = user.roles || [];
        this.userEmail = user.email || '';
        this.isAdmin = this.userRoles.some(role => 
          role === 'ADMIN' || 
          role === 'ROLE_ADMIN' || 
          role === 'admin' ||
          role === 'ROLE_ADMIN' ||
          role === 'Admin'
        );
        
        console.log('👤 Informations utilisateur :', {
          id: this.currentUserId,
          roles: this.userRoles,
          isAdmin: this.isAdmin,
          email: this.userEmail,
          username: user.username
        });
        
        // Si admin n'est pas détecté, afficher un avertissement
        if (!this.isAdmin && this.userRoles.length > 0) {
          console.warn('⚠️ Vous n\'êtes pas reconnu comme administrateur. Les rôles disponibles sont :', this.userRoles);
          console.warn('👉 La suppression peut échouer si le backend attend un rôle différent (ex: ROLE_ADMIN).');
        }
        if (this.isAdmin) {
          console.log('✅ Utilisateur reconnu comme administrateur. La suppression devrait fonctionner.');
        }
      } else {
        console.warn('⚠️ Aucun utilisateur connecté trouvé dans sessionStorage.');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des infos utilisateur:', error);
    }
  }

  /**
   * Restore state from URL parameters
   */
  private restoreStateFromUrl(): void {
    this.route.queryParams.subscribe(params => {
      this.currentPage = params['page'] ? +params['page'] : 1;
      this.selectedType = params['type'] || 'all';
      this.searchTerm = params['search'] || '';
    });
  }

  /**
   * Update URL with current state
   */
  private updateUrlState(): void {
    const queryParams: any = {};
    if (this.currentPage !== 1) queryParams['page'] = this.currentPage;
    if (this.selectedType !== 'all') queryParams['type'] = this.selectedType;
    if (this.searchTerm.trim()) queryParams['search'] = this.searchTerm;

    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge'
    });
    this.location.replaceState(urlTree.toString());
  }

  /**
   * Load all materials from API
   */
  loadMaterials(): void {
    this.isLoading = true;
    this.errorMessage = '';
    
    this.agenciesService.getListMateriaux().subscribe({
      next: (response: any) => {
        console.log("All materials API response:", response);
        this.handleApiResponse(response);
      },
      error: (error) => {
        console.error("Error loading all materials:", error);
        this.agenciesService.getListAgenceImmob().subscribe({
          next: (response: any) => {
            this.handleApiResponse(response);
          },
          error: (err) => {
            this.handleApiError(err);
          }
        });
      }
    });
  }

  /**
   * Load materials by type
   */
  loadMaterialsByType(typeId: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.selectedType = typeId;
    this.currentPage = 1;
    this.updateUrlState();
    
    if (typeId === 'all') {
      this.loadMaterials();
    } else {
      switch(typeId) {
        case 'equipement':
          this.agenciesService.getMateriauxEquipement().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        case 'automatisme':
          this.agenciesService.getMateriauxAutomatisme().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        case 'chauffage':
          this.agenciesService.getMateriauxChauffage().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        case 'energie':
          this.agenciesService.getMateriauxEnergie().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        case 'meuble':
          this.agenciesService.getMateriauxMeuble().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        case 'protection':
          this.agenciesService.getMateriauxProtection().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        case 'menuiserie':
          this.agenciesService.getMateriauxMenuiserie().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        case 'decoration':
          this.agenciesService.getMateriauxDecoration().subscribe({
            next: (response: any) => this.handleApiResponse(response),
            error: (error) => this.handleApiError(error)
          });
          break;
        default:
          this.loadMaterials();
      }
    }
  }

  /**
   * Handle API response
   */
  private handleApiResponse(response: any): void {
    try {
      let materialsData: any[] = [];
      
      if (Array.isArray(response)) {
        materialsData = response;
      } else if (response && typeof response === 'object') {
        if (response.data && Array.isArray(response.data)) {
          materialsData = response.data;
        } else {
          const arrayValues = Object.values(response).filter(val => Array.isArray(val));
          if (arrayValues.length > 0) {
            materialsData = arrayValues[0] as any[];
          }
        }
      }
      
      console.log("Total items received:", materialsData.length);
      
      if (this.selectedType !== 'all') {
        materialsData = materialsData.filter(material => 
          material.categorie?.toLowerCase() === this.selectedType.toLowerCase()
        );
      }
      
      this.allMaterials = materialsData;
      
      this.allMaterials.sort((a: any, b: any) => {
        const aProduits = Array.isArray(a.produits) ? a.produits.length : 0;
        const bProduits = Array.isArray(b.produits) ? b.produits.length : 0;
        if (aProduits === 0 && bProduits > 0) return 1;
        if (aProduits > 0 && bProduits === 0) return -1;
        return 0;
      });
      
      this.applyFilters();
      this.isLoading = false;
      this.dataLoaded = true;
    } catch (error) {
      console.error('Error processing response:', error);
      this.errorMessage = 'Erreur lors du traitement des données';
      this.allMaterials = [];
      this.applyFilters();
      this.isLoading = false;
      this.dataLoaded = true;
    }
  }

  /**
   * Handle API error
   */
  private handleApiError(error: any): void {
    console.error('Error loading materials:', error);
    this.errorMessage = error.message || 'Erreur lors du chargement des matériaux';
    this.isLoading = false;
    this.dataLoaded = true;
    this.allMaterials = [];
    this.applyFilters();
  }

  /**
   * Get count for a specific material type
   */
  getTypeCount(typeId: string): number {
    if (typeId === 'all') return this.allMaterials.length;
    return this.allMaterials.filter(material => 
      material.categorie?.toLowerCase() === typeId.toLowerCase()
    ).length;
  }

  /**
   * Get total produits count
   */
  getTotalProduits(): number {
    return this.allMaterials.reduce((total, material) => {
      return total + (Array.isArray(material.produits) ? material.produits.length : 0);
    }, 0);
  }

  /**
   * Apply filters (search only)
   */
  applyFilters(): void {
    let filtered = [...this.allMaterials];
    
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(material => {
        const nameMatch = material.name?.toLowerCase().includes(searchLower) || false;
        const addressMatch = material.adresse?.toLowerCase().includes(searchLower) || false;
        const emailMatch = material.email?.toLowerCase().includes(searchLower) || false;
        const phoneMatch = material.phone_number?.toString().includes(searchLower) || false;
        const roleMatch = material.role?.toLowerCase().includes(searchLower) || false;
        const categorieMatch = material.categorie?.toLowerCase().includes(searchLower) || false;
        return nameMatch || addressMatch || emailMatch || phoneMatch || roleMatch || categorieMatch;
      });
    }
    
    this.filteredMaterials = filtered;
    this.totalItems = filtered.length;
    
    const maxPage = Math.ceil(this.totalItems / this.itemsPerPage);
    if (this.currentPage > maxPage && maxPage > 0) {
      this.currentPage = maxPage;
      this.updateUrlState();
    }
  }

  /**
   * Perform search
   */
  performSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    this.selectedType = 'all';
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadMaterials();
    this.updateUrlState();
  }

  /**
   * Get type label from categorie value
   */
  getTypeLabel(categorieValue: string): string {
    if (!categorieValue) return 'Non spécifié';
    const type = this.materiaux.find(m => m.value === categorieValue.toLowerCase());
    return type ? type.label : categorieValue.charAt(0).toUpperCase() + categorieValue.slice(1);
  }

  /**
   * Handle page change
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.updateUrlState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(size: number): void {
    this.itemsPerPage = size;
    this.currentPage = 1;
    this.applyFilters();
    this.updateUrlState();
  }

  /**
   * Get display range for pagination info
   */
  getDisplayRange(): { start: number, end: number } {
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    return { start, end };
  }

  /**
   * Handle image error
   */
  handleImageError(event: any): void {
    event.target.src = 'assets/default-material.png';
  }

  /**
   * ✅ Suppression avec diagnostic complet
   */
  deleteMaterial(material: any, index: number): void {
    const materialId = material.id || material._id;
    
    if (!materialId) {
      Swal.fire({
        title: "Erreur!",
        text: "Ce fournisseur n'a pas d'identifiant valide.",
        icon: "error"
      });
      return;
    }
    
   
    
    const materialName = material.name || material.nom || 'ce fournisseur';
    
    Swal.fire({
      title: `Supprimer ${materialName}?`,
      text: "Cette action est irréversible et supprimera également tous les produits associés.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff4757",
      cancelButtonColor: "#2ed573",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      reverseButtons: true,
      backdrop: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        console.log(`🗑️ Tentative de suppression de l'ID ${materialId} avec le token de l'utilisateur ${this.userEmail} (admin: ${this.isAdmin})`);
        
        this.agenciesService.deleteItem(materialId).subscribe({
          next: () => {
            Swal.fire({
              title: "Supprimé!",
              text: "Le fournisseur a été supprimé avec succès.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
            
            // Mettre à jour les listes
            this.allMaterials = this.allMaterials.filter(item => {
              const itemId = item.id || item._id;
              return itemId !== materialId;
            });
            this.filteredMaterials = this.filteredMaterials.filter(item => {
              const itemId = item.id || item._id;
              return itemId !== materialId;
            });
            
            this.applyFilters();
          },
          error: (error) => {
            console.error('❌ Erreur détaillée lors de la suppression :', error);
            console.error('🔍 Détails de l\'erreur :', {
              status: error.status,
              message: error.message,
              error: error.error
            });
            
            let errorMsg = 'Erreur inconnue lors de la suppression.';
            let statusCode = '';
            
            if (error instanceof Error) {
              errorMsg = error.message;
              statusCode = errorMsg.match(/\d{3}/)?.[0] || '';
            } else if (typeof error === 'string') {
              errorMsg = error;
              statusCode = errorMsg.match(/\d{3}/)?.[0] || '';
            } else if (error.error && typeof error.error === 'string') {
              errorMsg = error.error;
              statusCode = errorMsg.match(/\d{3}/)?.[0] || '';
            }
            
            // Personnalisation du message selon le code d'erreur
            if (statusCode === '403' || errorMsg.includes('403') || errorMsg.toLowerCase().includes('forbidden')) {
              errorMsg = `❌ Erreur 403 - Accès interdit

Rôles actuels : ${this.userRoles.join(', ') || 'Aucun rôle trouvé'}
Email : ${this.userEmail}
Admin : ${this.isAdmin ? '✅ Oui' : '❌ Non'}

💡 Si vous êtes administrateur, vérifiez que votre token JWT contient bien le rôle "ADMIN" ou "ROLE_ADMIN".
💡 Le backend peut exiger un rôle spécifique dans Spring Security (@PreAuthorize).
💡 Vérifiez la configuration du token dans la console (F12 > Application > Session Storage).`;
            } else if (statusCode === '401' || errorMsg.includes('401')) {
              errorMsg = `❌ Erreur 401 - Non autorisé

Votre session a peut-être expiré. Veuillez vous reconnecter.
Email : ${this.userEmail}`;
            } else if (statusCode === '404' || errorMsg.includes('404')) {
              errorMsg = `❌ Erreur 404 - Fournisseur introuvable

L'ID ${materialId} n'existe plus dans la base de données.
La liste va être rafraîchie.`;
              // Rafraîchir la liste après quelques secondes
              setTimeout(() => this.loadMaterials(), 2000);
            } else if (statusCode === '500' || errorMsg.includes('500')) {
              errorMsg = `❌ Erreur 500 - Erreur serveur

Le serveur a rencontré une erreur interne. Veuillez réessayer plus tard.`;
            }
            
            Swal.fire({
              title: "Erreur!",
              html: errorMsg.replace(/\n/g, '<br>'),
              icon: "error"
            });
          }
        });
      }
    });
  }

  /**
   * Get status color based on active status
   */
  getStatusColor(active: boolean): string {
    return active ? '#2ecc71' : '#e74c3c';
  }

  /**
   * Get status label
   */
  getStatusLabel(active: boolean): string {
    return active ? 'Actif' : 'Inactif';
  }
}